import type { ChangeEvent } from 'react';
import type { RGBColor } from 'react-color';
import _ from 'lodash';
import { changeMobileDesign } from '../../apis/firebase';
import { ERROR_CHANGE_DESIGN, SUCCESS_UPDATE_TEXT, INVALID_COLOR } from '../../constants';
import { showNotification } from '../../components/Notification/Notification.view';
import { TITLE_DESCRIPTION, CLIENT_SPACE, NEW_PRODUCTS_DESCRIPTION, LOYALTY_NAME } from '../../constants/dataConfig';
import { intersectionObjects } from '../../utils/shopUtils';
import { getALLMenus } from '../../apis/backend';
import store from '../../utils/store';
import {
  MobileDesignConfigExternalProps,
  MobileDesignConfigState,
  MobileDesignSection,
  MobileDesignSetState,
  MobileDesignProduct,
  SplashValidationMessage
} from './MobileDesignConfig.types';

export const MAX_SLIDER_IMAGES = 3;
export const MAX_FILE_SIZE = 500000;
export const ALLOWED_IMAGE_EXTENSIONS = ['.png', '.jpeg', '.jpg', 'jfif'];
export const HTTPS_PREFIX = 'https://';
export const HEX_COLOR_REGEX = /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/i;
export const PRODUCTS_CONFIG_KEY = 'Produits';

export const validateHexColor = (color: string): boolean => {
  if (!color || typeof color !== 'string') {
    return false;
  }
  const trimmed = color.trim();
  if (!trimmed) {
    return false;
  }
  return HEX_COLOR_REGEX.test(trimmed) && trimmed.startsWith('#');
};

export const normalizeHexColor = (color: string): string => {
  if (!color) {
    return '';
  }
  const trimmed = color.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
};

export const processColorInputChange = (
  value: string
): { isValid: boolean; normalizedValue: string; validationMessage: SplashValidationMessage | null } => {
  const normalized = normalizeHexColor(value);
  const isValid = !normalized || validateHexColor(normalized);
  
  return {
    isValid,
    normalizedValue: normalized,
    validationMessage: normalized ? { text: isValid ? 'is-valid' : 'is-invalid' } : null
  };
};

export const processColorPickerChange = (
  color: { hex: string; rgb: RGBColor }
): { hex: string; rgb: RGBColor; isValid: boolean } => {
  return {
    hex: color.hex,
    rgb: color.rgb,
    isValid: validateHexColor(color.hex)
  };
};

export const transformSectionsToArray = (
  sections: MobileDesignSection[] | Record<string, MobileDesignSection>
): MobileDesignSection[] => {
  if (Array.isArray(sections)) {
    return sections;
  }
  if (!sections) {
    return [];
  }
  return Object.values(sections);
};

export const aggregateProductsFromSections = (
  sections: MobileDesignSection[],
  productShopIdMap?: Map<string, string>
): MobileDesignProduct[] => {
  const aggregated: MobileDesignProduct[] = [];
  _.forEach(sections, section => {
    _.forEach(section.products, product => {
      const shopId = productShopIdMap?.get(product.key);
      aggregated.push({ 
        sectionKey: section?.key, 
        shopId: shopId,
        ...product 
      });
    });
  });
  return aggregated.sort((a, b) => -`${b.sectionKey}`.localeCompare(`${a.sectionKey}`));
};

export const filterSectionsBySearchTerm = (
  allSections: MobileDesignSection[] | Record<string, MobileDesignSection>,
  searchTerm: string
): Record<string, MobileDesignSection> => {
  const sectionsArray = transformSectionsToArray(allSections);
  const filteredSections: Record<string, MobileDesignSection> = {};
  const lowerSearchTerm = searchTerm?.toLowerCase() || '';
  
  _.forEach(sectionsArray, section => {
    _.forEach(section.products, product => {
      if (product?.name?.toLowerCase().includes(lowerSearchTerm)) {
        if (!filteredSections[section.key]) {
          filteredSections[section.key] = {
            ...section,
            products: {}
          };
        }
        filteredSections[section.key].products[product.key] = product;
      }
    });
  });
  
  
  return filteredSections;
};

export const formatProductsForPayload = (
  productsObject: Record<string, MobileDesignProduct>,
  produits: MobileDesignProduct[],
  productShopIdMap: Map<string, string>,
  defaultShopId: string
): { productKey: string; sectionKey: string; shopId: string }[] => {
  const productSectionMap = new Map(produits.map(p => [p.key, p]));

  const productsArray = Object.values(productsObject)
    .filter((product): product is MobileDesignProduct => Boolean(product?.key))
    .map(product => {
      const productWithSection = productSectionMap.get(product.key);
      const shopId = productShopIdMap.get(product.key) ||
        (productWithSection?.shopId as string) ||
        defaultShopId;
      return {
        productKey: product.key,
        sectionKey: (productWithSection?.sectionKey as string) || '',
        shopId: shopId
      };
    })
    .filter(item => item.sectionKey && item.shopId);
  return productsArray;
};



export const transformDesignStateToPayload = (
  stateObj: MobileDesignConfigState,
  produits: MobileDesignProduct[],
  images: {
    pictures: (string | null)[];
    fedilityPicture: string | null;
    myAccountImage: string | null;
    myOrdersImage: string | null;
  },
  selectedProduct?: number
): Record<string, unknown> => {
  const {
    selected,
    products = [],
    titleDescription,
    clientSpace,
    url,
    facebook,
    linkedin,
    instagram,
    name,
    newProductsDescription,
    splashBackgroundColor
  } = stateObj;

  const {
    pictures,
    fedilityPicture,
    myAccountImage,
    myOrdersImage
  } = images;

  const productStyle = selectedProduct === 1 ? 2 : 1;

  const data: Record<string, unknown> = {
    selected,
    pictures,
    products: intersectionObjects(Object.values(products), produits),
    titleDescription,
    clientSpace,
    url: getValidUrl(url),
    facebook: getValidUrl(facebook),
    linkedin: getValidUrl(linkedin),
    productStyle,
    instagram: getValidUrl(instagram),
    name,
    newProductsDescription,
    fedilityPicture,
    myAccountImage,
    myOrdersImage
  };

  if (splashBackgroundColor && typeof splashBackgroundColor === 'string' && splashBackgroundColor.trim() !== '') {
    data.splashBackgroundColor = splashBackgroundColor;
  }

  return data;
};

export const validateDesignData = (
  data: Record<string, unknown>,
  openErrorModal: (message: string) => void
): boolean => {
  const splashBackgroundColor = data.splashBackgroundColor as string | undefined;
  
  if (splashBackgroundColor && typeof splashBackgroundColor === 'string' && splashBackgroundColor.trim() !== '') {
    if (!validateHexColor(splashBackgroundColor)) {
      openErrorModal(INVALID_COLOR);
      return false;
    }
  }
  
  return true;
};

type MenuSection = Omit<MobileDesignSection, 'products'> & {
  products: Record<
    string,
    MobileDesignProduct & {
      disabled?: boolean;
      disabledCC?: boolean;
      disabledCS?: boolean;
      disabledDelivery?: boolean;
    }
  >;
  hidden?: boolean;
};

interface MenuResponse {
  shopId?: string;
  sections: MenuSection[];
  [key: string]: unknown;
}

export const initStates = async (
  props: MobileDesignConfigExternalProps,
  setStateObj: MobileDesignSetState
) => {
  const {
    url = null,
    facebook = null,
    instagram = null,
    linkedin = null,
    titleDescription = TITLE_DESCRIPTION,
    clientSpace = CLIENT_SPACE,
    name = LOYALTY_NAME,
    newProducts,
    newProductsDescription = NEW_PRODUCTS_DESCRIPTION
  } = props ?? {};
  await setStateObj({
    url,
    isLoading: false,
    isLoadingProducts: false,
    facebook,
    instagram,
    linkedin,
    titleDescription,
    clientSpace,
    name,
    products: newProducts,
    newProductsDescription
  });
};

export const changeDesignMobile = async (
  stateObj: MobileDesignConfigState,
  produits: MobileDesignProduct[],
  images: {
    pictures: (string | null)[];
    fedilityPicture: string | null;
    myAccountImage: string | null;
    myOrdersImage: string | null;
  },
  setStateObj: MobileDesignSetState,
  props: MobileDesignConfigExternalProps & { config?: string; selectedProduct?: number }
) => {
  const data = transformDesignStateToPayload(stateObj, produits, images, props.selectedProduct);
  data.enabled = props?.enabled;

  if (!validateDesignData(data, props.openErrorModal)) {
    return;
  }

  if (props.config === PRODUCTS_CONFIG_KEY && data.products) {
    const productsObject = data.products as Record<string, MobileDesignProduct>;
    const productShopIdMap = stateObj.productShopIdMap || new Map<string, string>();

    const productsArray = formatProductsForPayload(
      productsObject,
      produits,
      productShopIdMap,
      store.getState().user.shopId
    );
    data.products = productsArray;
  }

  try {
    setStateObj({ isLoading: true });
    await changeMobileDesign(data, props.config);
    showNotification(
      'light',
      'ltr',
      'primary',
      SUCCESS_UPDATE_TEXT,
      `Les paramétres de design application mobile ont bien été modifiés.`
    );
    setStateObj({
      loadingCropper: stateObj.selected === 0 && true
    });
  } catch (e) {
    props.openErrorModal(ERROR_CHANGE_DESIGN);
  }
  setStateObj({ isLoading: false });
};
export const normalizeMenusData = (menusData: unknown): MenuResponse[] => {
  if (Array.isArray(menusData)) {
    return menusData;
  }
  if (menusData && typeof menusData === 'object') {
    return Object.entries(menusData).map(([shopId, menu]) => ({
      ...(menu as MenuResponse),
      shopId,
    }));
  }
  return [];
};

export const getSections = async (
  setStateObj: MobileDesignSetState
): Promise<{ sections: MobileDesignSection[]; productShopIdMap: Map<string, string> }> => {
  setStateObj({ isLoadingProducts: true });
  const menusData = await getALLMenus();
  
  const menus = normalizeMenusData(menusData);
  const productShopIdMap = new Map<string, string>();

  _.forEach(menus, (menu) => {
    const shopId = menu.shopId;
    if (shopId && menu.sections) {
      _.forEach(menu.sections, section => {
        if (section.products) {
          _.forEach(Object.keys(section.products), productKey => {
            productShopIdMap.set(productKey, shopId);
          });
        }
      });
    }
  });
  
  const sections = _.map(menus, elm => {
    return getSectionsWithEnabledProducts(elm.sections);
  });
  let sectionWithEnabledProducts = sections[0] ?? [];
  for (let i = 1; i < sections.length; i++) {
    sectionWithEnabledProducts = _.unionBy(sectionWithEnabledProducts, sections[i], 'key');
    sectionWithEnabledProducts = _.uniqBy(sectionWithEnabledProducts, 'name');
  }
  setStateObj({ isLoadingProducts: false });
  return { 
    sections: _.sortBy(sectionWithEnabledProducts, o => o.name),
    productShopIdMap 
  };
};

export const handleCheckBox = (
  products: Record<string, MobileDesignProduct>,
  setStateObj: MobileDesignSetState,
  value: MobileDesignProduct
) => {
  const productExists = value.key in products;

  if (productExists) {
    const { [value.key]: _, ...remainingProducts } = products;
    setStateObj({ products: remainingProducts });
  } else {
    setStateObj({ products: { ...products, [value.key]: value } });
  }
};

export const handleSearchChange = (
  e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  setStateObj: MobileDesignSetState,
  setSearchTerm: (value: string) => void,
  stateObj: MobileDesignConfigState
) => {
  setSearchTerm(e.target.value);
  handleSearchBlur(setStateObj, e.target.value, stateObj);
};

export const handleSearchBlur = async (
  setStateObj: MobileDesignSetState,
  searchTerm: string,
  stateObj: MobileDesignConfigState
) => {
  const filteredSections = filterSectionsBySearchTerm(stateObj.allSections, searchTerm);
  setStateObj({ sections: filteredSections });
};



const getEnabledProducts = (
  products: Record<
    string,
    MobileDesignProduct & {
      disabled?: boolean;
      disabledCC?: boolean;
      disabledCS?: boolean;
      disabledDelivery?: boolean;
    }
  >
) => {
  return _.filter(_.keys(products), elm =>
    !(products[elm].disabled || (products[elm].disabledCC &&
      products[elm].disabledCS && products[elm].disabledDelivery))
  ).reduce((obj, cur) => ({ ...obj, [cur]: products[cur] }), {});
};

export const getSectionsWithEnabledProducts = (commonSections: MenuSection[]) => {
  const enabledSections: Record<string, MobileDesignSection> = {};
  _.map(commonSections, section => {
    if (!section.hidden) {
      _.map(section.products, product => {
        _.assign(enabledSections, {
          [section.key]: {
            ...section,
            products: getEnabledProducts(section.products)
          }
        });
      });
    }

  });

  return _.filter(enabledSections, section => _.size(section.products) > 0);
};


export const getValidUrl = (link?: string | null): string | null => {
  if (!link) {
    return null;
  }
  const value = link.trim();
  if (!value) {
    return null;
  }
  if (value.indexOf(HTTPS_PREFIX) === -1) {
    return `${HTTPS_PREFIX}${value}`;
  }
  return value;
};
